import { Form, Formik } from 'formik';
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationCircle } from 'react-icons/all';
import { useDispatch, useSelector } from 'react-redux';
import { PaymentCard, ProductTypeToPayFor } from '../../../../@posturinn/client-react';
import { closeAllModals } from '../../../../actions/modalActions';
import { getAccountUsesNewFlow, getPaymentCard, payWithCard, startNewFlow } from '../../../../actions/paymentActions';
import useShipmentFees from '../../../../hooks/useShipmentFees';
import { ReactComponent as IconCard } from '../../../../icons/card_default.svg';
import { ReactComponent as IconMasterCard } from '../../../../icons/card_mastercard.svg';
import { ReactComponent as IconVisa } from '../../../../icons/card_visa.svg';
import { ReactComponent as Close } from '../../../../icons/close-button.svg';
import IPaymentReducer from '../../../../interfaces/reducers/paymentReducer';
import { IShipmentFees } from '../../../../interfaces/reducers/shipmentsReducer';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../../reducers';
import Button from '../../../base/button/button';
import FormikFieldWrapper from '../../../base/forms/formikFieldWrapper';
import { StyledFormikField } from '../../../base/forms/input';
import { Label } from '../../../base/forms/label';
import { Column, Columns } from '../../../base/grid';
import { A } from '../../../base/typography';
import { RawLoader } from '../../../loading/loading';
import * as StyledModals from '../myShipmentsModals.styles';
import { paymentOptionInitialValues, paymentOptionSchema } from '../paymentOption/paymentOption.schema';
import * as StyledPay from './pay.styles';

interface Props {}

const Pay: FC<Props> = () => {
	const { t } = useTranslation('myShipments');
	const dispatch = useDispatch<any>();
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { modalSubmitPending, modalSubmitError, modalSubmitErrorDetails } = userProfileReducer;
	const paymentReducer: IPaymentReducer = useSelector<IStore, IPaymentReducer>(store => store.paymentReducer);
	const { paymentCardLoading, paymentCardError, paymentCard, accountUsesNewFlow, accountUsesNewFlowLoading } =
		paymentReducer;

	useEffect(() => {
		if (paymentCard === null) {
			dispatch(getPaymentCard());
			dispatch(getAccountUsesNewFlow());
		}
	}, [paymentCard, dispatch]);

	const closeModal = () => {
		dispatch(closeAllModals());
		document.body.style.overflow = 'unset';
	};
	document.body.style.overflow = 'hidden';

	const { modalHeading, shipmentId, page, index } = useSelector(({ modalReducer }) => modalReducer);

	const {
		shipmentFees,
		shipmentFeesLoading,
		shipmentFeesError,
	}: {
		shipmentFees: IShipmentFees;
		shipmentFeesLoading: boolean;
		shipmentFeesError: boolean;
	} = useShipmentFees(shipmentId);

	const fees = shipmentFees[shipmentId];

	const usesNewFlow = accountUsesNewFlow?.usesNewFlow;

	const getTotal = f => {
		let feesTotal = 0;
		f.forEach(fee => (fee.isPaid ? null : (feesTotal += fee.grossAmount)));
		return feesTotal;
	};

	const pay = () => {
		dispatch(
			payWithCard(paymentCard, [shipmentId], ProductTypeToPayFor.InboundShipment, '998', false, page, index)
		);
	};

	const payWithNewCard = values => {
		const newPaymentCard = new PaymentCard();
		newPaymentCard.cardholderName = values.cardholderName;
		newPaymentCard.cardNumber = values.cardNumber;
		newPaymentCard.expiryMonth = values.expiryDate.substring(0, 2);
		newPaymentCard.expiryYear = values.expiryDate.substring(values.expiryDate.length - 2);
		newPaymentCard.securityCode = values.cvc;

		if (usesNewFlow) {
			dispatch(
				startNewFlow(
					newPaymentCard,
					[shipmentId],
					ProductTypeToPayFor.InboundShipment,
					'998',
					values.save,
					page,
					index
				)
			);
			return;
		}

		dispatch(
			payWithCard(
				newPaymentCard,
				[shipmentId],
				ProductTypeToPayFor.InboundShipment,
				'998',
				values.save,
				page,
				index
			)
		);
	};

	return (
		<StyledModals.Wrapper>
			<StyledModals.Heading>
				<div>{modalHeading}</div>
				<A onClick={closeModal}>
					<Close />
				</A>
			</StyledModals.Heading>

			{modalSubmitError && (
				<StyledModals.ErrorMessage>
					<FaExclamationCircle />
					<div>{modalSubmitErrorDetails !== '' ? modalSubmitErrorDetails : t('genericError')}</div>
				</StyledModals.ErrorMessage>
			)}

			<Label marginTop="3rem">{t('priceBreakdown')}</Label>
			<StyledPay.ExpenseWrapper>
				{!shipmentFeesLoading && !shipmentFeesError && fees && (
					<>
						{fees.map(
							(fee, i) =>
								!fee.isPaid && (
									<StyledPay.BorderColumns key={i}>
										<StyledPay.TextColumn mobile={2 / 3}>{fee.description}</StyledPay.TextColumn>
										<StyledPay.TextColumn mobile={1 / 3} right>
											{fee.grossAmount}
											kr.
										</StyledPay.TextColumn>
									</StyledPay.BorderColumns>
								)
						)}
						<StyledPay.BorderColumns>
							<StyledPay.TextColumn mobile={1 / 2} secondary>
								{t('total')}
							</StyledPay.TextColumn>
							<StyledPay.TextColumn mobile={1 / 2} secondary right>
								{getTotal(fees)}
								kr.
							</StyledPay.TextColumn>
						</StyledPay.BorderColumns>
					</>
				)}
			</StyledPay.ExpenseWrapper>

			{(shipmentFeesLoading || paymentCardLoading || accountUsesNewFlowLoading) && <RawLoader />}

			{!paymentCardLoading && !paymentCardError && paymentCard === null && (
				<StyledPay.FormWrapper>
					<Formik
						onSubmit={payWithNewCard}
						initialValues={paymentOptionInitialValues}
						validationSchema={paymentOptionSchema}>
						<Form>
							<Columns>
								<Column>
									<FormikFieldWrapper
										label={`${t('cardholder')} *`}
										name="cardholderName"
										languageComponent="myShipments"
										smallLabel>
										<StyledFormikField
											type="text"
											id="cardholderName"
											name="cardholderName"
											placeholder={t('cardholderPlaceholder')}
											autoComplete="name"
										/>
									</FormikFieldWrapper>
								</Column>
							</Columns>
							<Columns>
								<Column>
									<FormikFieldWrapper
										label={`${t('cardnumber')} *`}
										name="cardNumber"
										languageComponent="myShipments"
										smallLabel>
										<StyledFormikField
											type="text"
											id="cardNumber"
											name="cardNumber"
											placeholder={t('cardnumberPlaceholder')}
										/>
									</FormikFieldWrapper>
								</Column>
							</Columns>
							<Columns>
								<Column mobile={1 / 2}>
									<FormikFieldWrapper
										label={`${t('expiryDate')} *`}
										name="expiryDate"
										languageComponent="myShipments"
										smallLabel>
										<StyledFormikField
											type="text"
											id="expiryDate"
											name="expiryDate"
											placeholder={t('expiryDatePlaceholder')}
										/>
									</FormikFieldWrapper>
								</Column>
								<Column mobile={1 / 2}>
									<FormikFieldWrapper
										label={`${t('cvc')} *`}
										name="cvc"
										languageComponent="myShipments"
										smallLabel>
										<StyledFormikField
											type="text"
											id="cvc"
											name="cvc"
											placeholder={t('cvcPlaceholder')}
											autoComplete="off"
										/>
									</FormikFieldWrapper>
								</Column>
								{/* TODO <Column>
										<FormikFieldWrapper name="save" languageComponent="myShipments">
											<FormikCheckbox
												id="save"
												name="save"
												label={t('save')}
												setFieldValue={setFieldValue}
											/>
										</FormikFieldWrapper>
									</Column> */}
								<Column>
									<Button primary type="submit" isLoading={modalSubmitPending}>
										{t('pay')}
									</Button>
								</Column>
							</Columns>
						</Form>
					</Formik>
				</StyledPay.FormWrapper>
			)}
			{!paymentCardLoading && paymentCardError && (
				<StyledModals.ErrorMessage>{t('genericError')}</StyledModals.ErrorMessage>
			)}
			{/*TODO: what to do when there is an error  accountUsesNewFlowError && <StyledModals.ErrorMessage>{t('genericError')}</StyledModals.ErrorMessage>*/}
			{!paymentCardLoading && !paymentCardError && paymentCard && (
				<>
					<StyledPay.CardContainer>
						{{ Visa: <IconVisa />, MasterCard: <IconMasterCard /> }[paymentCard.cardType] || <IconCard />}
						{t('payWith')}
						{paymentCard.cardNumber}
						{/*<div><A>Breyta</A></div>*/}
					</StyledPay.CardContainer>
					<Button primary disabled={!paymentCard || modalSubmitPending} onClick={pay}>
						{t('pay')}
					</Button>
				</>
			)}
		</StyledModals.Wrapper>
	);
};

export default Pay;
